import { supabase } from '../lib/supabase';
import { fetchInChunks } from './supabaseChunkedIn';
import {
  normalizeImageForUpload,
  assertSupportedUploadFormat,
  UnsupportedImageFormatError,
} from './heicConvert';

export type ProjectAssetType = 'image' | 'video' | 'audio';

export interface ProjectAsset {
  id: string;
  project_id: string;
  id_company: string;
  user_id: string | null;
  type: ProjectAssetType;
  name: string | null;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  thumbnail_path: string | null;
  created_at: string;
}

const BUCKET = 'video-project-assets';

// In-memory cache of signed URLs by storage path (50min TTL to stay safe under 1h expiry)
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();
const SIGNED_URL_TTL_SECONDS = 3600;
const SIGNED_URL_CACHE_MS = 50 * 60 * 1000;

// La caché también sobrevive a recargar la pestaña (sessionStorage). No es sólo
// ahorrarse el pedido de firma: la MISMA URL deja que el navegador reuse lo que ya
// bajó del video, y una firma nueva es una URL nueva, que siempre se baja de cero.
// Cada entrada guarda su vencimiento, así que nunca se reusa una vencida.
// ADR-2026-11-03-el-estudio-abre-rapido.
const CLAVE_FIRMAS = 'atlas:firmas-de-assets';
const MAX_FIRMAS_GUARDADAS = 400;
try {
  const crudo = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(CLAVE_FIRMAS) : null;
  if (crudo) {
    const ahora = Date.now();
    for (const [path, v] of Object.entries(JSON.parse(crudo) as Record<string, { url: string; expiresAt: number }>)) {
      if (v && typeof v.url === 'string' && v.expiresAt > ahora) signedUrlCache.set(path, v);
    }
  }
} catch {
  // sin sessionStorage (modo privado, bloqueado): queda sólo en memoria
}
function persistirFirmas() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const entradas = [...signedUrlCache.entries()].slice(-MAX_FIRMAS_GUARDADAS);
    sessionStorage.setItem(CLAVE_FIRMAS, JSON.stringify(Object.fromEntries(entradas)));
  } catch {
    // lleno o bloqueado: no pasa nada, se vuelve a firmar
  }
}
function guardarFirma(path: string, url: string, expiresAt: number) {
  signedUrlCache.set(path, { url, expiresAt });
  persistirFirmas();
}

/**
 * Firma VARIOS archivos de una sola vez (un pedido por cada 100 en vez de uno por
 * archivo), reusando las firmas que siguen vigentes. Lo usa la apertura de un proyecto
 * del Estudio, que antes firmaba clip por clip y esperaba a que volvieran todos
 * (ADR-2026-11-03-el-estudio-abre-rapido). Devuelve `path → url`; los que no se
 * pudieron firmar no están.
 */
export async function firmarVariosAssets(paths: string[]): Promise<Map<string, string>> {
  const firmadas = new Map<string, string>();
  const ahora = Date.now();
  const faltan: string[] = [];
  for (const path of new Set(paths)) {
    const cached = signedUrlCache.get(path);
    if (cached && cached.expiresAt > ahora) firmadas.set(path, cached.url);
    else faltan.push(path);
  }
  for (let i = 0; i < faltan.length; i += 100) {
    const tanda = faltan.slice(i, i + 100);
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(tanda, SIGNED_URL_TTL_SECONDS);
    if (error || !data) continue;
    for (const r of data) {
      if (!r.path || !r.signedUrl || r.error) continue;
      firmadas.set(r.path, r.signedUrl);
      signedUrlCache.set(r.path, { url: r.signedUrl, expiresAt: ahora + SIGNED_URL_CACHE_MS });
    }
  }
  if (faltan.length > 0) persistirFirmas();
  return firmadas;
}

/**
 * Las versiones livianas (proxies) de varios archivos, en UN pedido — el preview las usa
 * en vez del original (ADR-2026-11-03-el-preview-usa-la-version-liviana). Devuelve
 * ruta del ORIGINAL → URL de su versión liviana, sólo para las que existen (Storage
 * responde error por cada archivo que no está, y esa queda con el original).
 *
 * Caché PROPIA: nunca se guarda bajo la ruta del original, que es la clave que lee el
 * export (`signedUrlCache`). Las que no existen no se recuerdan: en la próxima apertura
 * se vuelven a buscar, por si ya se generaron.
 */
const firmasLivianas = new Map<string, { url: string; expiresAt: number }>();
export async function firmarVersionesLivianas(
  originales: string[],
  rutaDeLaLiviana: (original: string) => string,
): Promise<Map<string, string>> {
  const resultado = new Map<string, string>();
  const ahora = Date.now();
  const faltan: string[] = [];
  for (const original of new Set(originales)) {
    const cached = firmasLivianas.get(original);
    if (cached && cached.expiresAt > ahora) resultado.set(original, cached.url);
    else faltan.push(original);
  }
  for (let i = 0; i < faltan.length; i += 100) {
    const tanda = faltan.slice(i, i + 100);
    const deLaLiviana = new Map(tanda.map((o) => [rutaDeLaLiviana(o), o]));
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls([...deLaLiviana.keys()], SIGNED_URL_TTL_SECONDS);
    if (error || !data) continue;
    for (const r of data) {
      if (!r.path || !r.signedUrl || r.error) continue;
      const original = deLaLiviana.get(r.path);
      if (!original) continue;
      resultado.set(original, r.signedUrl);
      firmasLivianas.set(original, { url: r.signedUrl, expiresAt: ahora + SIGNED_URL_CACHE_MS });
    }
  }
  return resultado;
}

function extForFile(file: File): string {
  const m = file.name.match(/\.([a-zA-Z0-9]+)$/);
  if (m) return m[1].toLowerCase();
  // fall back to mime subtype
  const sub = file.type.split('/')[1];
  return sub || 'bin';
}

function typeForMime(mime: string): ProjectAssetType | null {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return null;
}

async function probeImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

async function probeMediaDuration(file: File, kind: 'video' | 'audio'): Promise<{ duration: number; width?: number; height?: number } | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(kind);
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const info: { duration: number; width?: number; height?: number } = { duration: el.duration };
      if (kind === 'video') {
        info.width = (el as HTMLVideoElement).videoWidth;
        info.height = (el as HTMLVideoElement).videoHeight;
      }
      resolve(info);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    el.src = url;
  });
}

export interface UploadAssetParams {
  file: File;
  projectId: string;
  companyId: string;
  userId?: string | null;
}

/**
 * Upload a file to the project assets bucket and insert a row into video_project_assets.
 * Returns the created asset row.
 */
export async function uploadProjectAsset({ file, projectId, companyId, userId }: UploadAssetParams): Promise<ProjectAsset> {
  // Rechazar formatos bloqueados (RAW/TIFF) con nuestro mensaje antes del gate
  // genérico "Unsupported file type" (un .cr2 no matchea typeForMime/guessType).
  assertSupportedUploadFormat(file);
  const type = typeForMime(file.type) || (file.type === '' ? guessTypeFromExt(file.name) : null);
  if (!type) {
    throw new Error(`Unsupported file type: ${file.type || file.name}`);
  }

  // HEIC/HEIF (iPhone) y AVIF (iPhone, cámaras modernas) no los manejan bien
  // el visor del editor, los pipelines de thumbnail ni los modelos de IA.
  // Los convertimos a JPEG acá antes de subir para que todo downstream
  // vea un archivo estándar, transparente al call-site.
  if (type === 'image') {
    file = await normalizeImageForUpload(file);
  }

  const ext = extForFile(file);
  // Use a random id for the file name. We'll let the DB assign the actual asset_id.
  // Generating uuid manually so we can use it for both path and row insert.
  const assetId = crypto.randomUUID();
  const storagePath = `${companyId}/${projectId}/${assetId}.${ext}`;

  // Probe dimensions/duration in parallel with the upload
  const probePromise: Promise<{ width?: number; height?: number; duration?: number } | null> =
    type === 'image'
      ? probeImageDimensions(file).then(d => (d ? { width: d.width, height: d.height } : null))
      : probeMediaDuration(file, type === 'video' ? 'video' : 'audio').then(d =>
          d ? { duration: d.duration, width: d.width, height: d.height } : null,
        );

  // Upload to storage
  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, { contentType: file.type || undefined, upsert: false });
  if (uploadErr) {
    throw new Error(`Storage upload failed: ${uploadErr.message}`);
  }

  const probed = await probePromise;

  // Insert row
  const row: Omit<ProjectAsset, 'created_at'> = {
    id: assetId,
    project_id: projectId,
    id_company: companyId,
    user_id: userId ?? null,
    type,
    name: file.name,
    storage_path: storagePath,
    mime_type: file.type || null,
    size_bytes: file.size,
    width: probed?.width ?? null,
    height: probed?.height ?? null,
    duration_seconds: probed?.duration ?? null,
    thumbnail_path: null,
  };

  const { data, error: insertErr } = await supabase
    .from('video_project_assets')
    .insert(row)
    .select('*')
    .single();

  if (insertErr || !data) {
    // Best-effort cleanup on insert failure
    await supabase.storage.from(BUCKET).remove([storagePath]).catch(() => {});
    throw new Error(`Asset row insert failed: ${insertErr?.message || 'unknown'}`);
  }

  return data as ProjectAsset;
}

function guessTypeFromExt(name: string): ProjectAssetType | null {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'avif', 'heic'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'webm', 'mkv', 'avi'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'].includes(ext)) return 'audio';
  return null;
}

/**
 * Traduce un error de `uploadProjectAsset` al mensaje que ve el usuario.
 *
 * Existe porque los call-sites del estudio atrapaban el error y solo hacían
 * `console.error`: subir un `.cr2` o un `.gif` a un slot no mostraba NADA —
 * el rechazo por formato tenía su mensaje desde siempre, pero moría en la
 * consola. Centralizarlo acá evita que cada punto de carga invente su propio
 * texto para el mismo problema.
 */
export function describeUploadError(err: unknown, fileName?: string): string {
  // Formato bloqueado (RAW/TIFF/GIF/SVG): ya trae el mensaje correcto.
  if (err instanceof UnsupportedImageFormatError) return err.message;
  const raw = err instanceof Error ? err.message : String(err);
  const cual = fileName ? `"${fileName}"` : 'ese archivo';
  if (raw.startsWith('Unsupported file type')) {
    return `No podemos usar ${cual}: no lo reconocemos como imagen, video ni audio. Probá con JPG, PNG, MP4 o MP3.`;
  }
  if (raw.startsWith('Storage upload failed') || raw.startsWith('Asset row insert failed')) {
    return `No se pudo subir ${cual}. Revisá tu conexión y probá de nuevo.`;
  }
  // Falla de conversión (el browser no pudo decodificar un HEIC/AVIF/WEBP).
  if (raw.includes('no pudo decodificar')) {
    return `No pudimos convertir ${cual}. Probá guardándolo como JPG o PNG.`;
  }
  return `No se pudo agregar ${cual}. Probá de nuevo.`;
}

/**
 * Get a cached signed URL for an asset's storage path. Refreshes if near expiry.
 */
export async function getAssetSignedUrl(storagePath: string): Promise<string | null> {
  const cached = signedUrlCache.get(storagePath);
  const now = Date.now();
  if (cached && cached.expiresAt > now) return cached.url;

  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) return null;

  guardarFirma(storagePath, data.signedUrl, now + SIGNED_URL_CACHE_MS);
  return data.signedUrl;
}

/**
 * Fetch asset rows by their IDs.
 */
export async function fetchAssetsByIds(ids: string[]): Promise<ProjectAsset[]> {
  if (ids.length === 0) return [];
  // Fetch en lotes para no exceder el largo de URL del gateway REST con
  // muchos ids (HTTP 400). Ver supabaseChunkedIn.
  let data: ProjectAsset[];
  try {
    data = await fetchInChunks<ProjectAsset>(ids, (chunk) =>
      supabase.from('video_project_assets').select('*').in('id', chunk),
    );
  } catch {
    return [];
  }
  return data as ProjectAsset[];
}

/**
 * Register an existing storage file as a project asset row (without re-uploading).
 * Used for AI-generated videos which are written directly to storage by the backend.
 * If a row with the same storage_path already exists for this project, returns it instead of inserting.
 */
export async function registerExistingAsset(params: {
  projectId: string;
  companyId: string;
  userId?: string | null;
  storagePath: string;
  type: ProjectAssetType;
  name?: string | null;
  mimeType?: string | null;
  durationSeconds?: number | null;
  width?: number | null;
  height?: number | null;
}): Promise<ProjectAsset | null> {
  const { projectId, companyId, userId, storagePath, type } = params;
  // Dedupe: if a row with the same storage_path already exists, return it.
  const { data: existing } = await supabase
    .from('video_project_assets')
    .select('*')
    .eq('project_id', projectId)
    .eq('storage_path', storagePath)
    .maybeSingle();
  if (existing) return existing as ProjectAsset;

  const id = crypto.randomUUID();
  const row: Omit<ProjectAsset, 'created_at'> = {
    id,
    project_id: projectId,
    id_company: companyId,
    user_id: userId ?? null,
    type,
    name: params.name ?? null,
    storage_path: storagePath,
    mime_type: params.mimeType ?? null,
    size_bytes: null,
    width: params.width ?? null,
    height: params.height ?? null,
    duration_seconds: params.durationSeconds ?? null,
    thumbnail_path: null,
  };
  const { data, error } = await supabase
    .from('video_project_assets')
    .insert(row)
    .select('*')
    .single();
  if (error || !data) return null;
  return data as ProjectAsset;
}

/**
 * Fetch all assets for a given project, optionally filtered by type(s). Newest first.
 */
export async function fetchProjectAssetsByType(
  projectId: string,
  types?: ProjectAssetType | ProjectAssetType[],
): Promise<ProjectAsset[]> {
  let q = supabase
    .from('video_project_assets')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (types) {
    const arr = Array.isArray(types) ? types : [types];
    if (arr.length > 0) q = q.in('type', arr);
  }
  const { data, error } = await q;
  if (error || !data) return [];
  return data as ProjectAsset[];
}

/**
 * Convenience: given a list of asset IDs, return an array of signed URLs in the same order.
 * Missing/unreadable assets resolve to null.
 */
export async function getSignedUrlsForAssetIds(ids: string[]): Promise<string[]> {
  if (ids.length === 0) return [];
  const assets = await fetchAssetsByIds(ids);
  const byId = new Map(assets.map(a => [a.id, a.storage_path]));
  const urls = await Promise.all(
    ids.map(async id => {
      const path = byId.get(id);
      if (!path) return null;
      return await getAssetSignedUrl(path);
    }),
  );
  return urls.filter((u): u is string => !!u);
}

/**
 * Delete an asset (storage file + DB row).
 */
export async function deleteProjectAsset(asset: Pick<ProjectAsset, 'id' | 'storage_path'>): Promise<void> {
  await supabase.storage.from(BUCKET).remove([asset.storage_path]).catch(() => {});
  await supabase.from('video_project_assets').delete().eq('id', asset.id);
  signedUrlCache.delete(asset.storage_path);
  persistirFirmas();
}
